/**
 * DashboardPage — post-login landing.
 *
 * Layout: 360px sidebar + map. Sidebar lists every project with a search box,
 * filter chips, and a per-row alarm badge. Map shows the same projects as
 * clickable pins on a satellite-streets Mapbox map (matching the editor's
 * style for visual consistency). Clicking a project — pin or list row —
 * loads it into projectStore and navigates to /editor.
 *
 * Platform admin: the site list spans every tenant, so the sidebar shows one
 * row per client and opens that client's project tickets in a second panel
 * docked to the right; the map narrows to the open client's pins. Admins can
 * also edit a project's name, location and capacity inline from its ticket.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProjects, listSiteCaptureDates, listAdminOrganizations, moveProject, setSiteLocation, deriveSiteCoordinates, updateProject, type SiteListItem, type UpdateProjectInput, type AdminOrganizationItem } from '../services/projectApi';
import { listAlarmSummary } from '../services/monitoringApi';
import { serverLogout } from '../services/apiFetch';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { useOpenProject } from '../hooks/useOpenProject';
import DashboardMap, { type AlarmCount } from '../components/dashboard/DashboardMap';
import DashboardSidebar, { type FilterKey, type SelectedClient } from '../components/dashboard/DashboardSidebar';
import { clientKeyOf, groupProjectsByClient } from '../components/dashboard/clientGroups';
import CreateProjectModal from '../components/common/CreateProjectModal';
import NotificationToasts from '../components/common/NotificationToasts';
import OrgSettingsPanel from '../components/editor/OrgSettingsPanel';
import TeamPanel from '../components/editor/TeamPanel';
import './DashboardPage.css';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { addNotification } = useUIStore();
  const userEmail = useAuthStore((s) => s.user?.email);
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const hasOrg = useAuthStore((s) => !!s.currentOrg);
  const [createOpen, setCreateOpen] = useState(false);
  const [orgSettingsOpen, setOrgSettingsOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [projects, setProjects] = useState<SiteListItem[]>([]);
  const [alarmCounts, setAlarmCounts] = useState<Record<string, AlarmCount>>({});
  const [captureDates, setCaptureDates] = useState<Record<string, string> | null>(null);
  // Platform admin: target organizations for the ticket's "Move" panel.
  const [adminOrgs, setAdminOrgs] = useState<AdminOrganizationItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [placeMode, setPlaceMode] = useState<{ siteId: string; siteName: string } | null>(null);
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null);
  const { loadingId, openProject, error: openError } = useOpenProject();

  // A failed project open must never be a silent dead click — surface the
  // hook's error as a notification. (ProjectSelectModal shows its own inline
  // banner; the dashboard had no visible error path until 2026-07-23.)
  useEffect(() => {
    if (openError) {
      addNotification({
        type: 'error',
        title: 'Could not open project',
        message: openError,
      });
    }
  }, [openError, addNotification]);

  // Debounce search (250 ms matches the modal's existing pattern).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Initial load: projects + alarm summary in parallel so the badges paint
  // with the pins (no flicker).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const refresh = useCallback(async (searchQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const [projectsRes, alarmsRes] = await Promise.all([
        // Backend SiteListQuerySchema caps `limit` at 100; the dashboard fits
        // the platform's expected scale (no real users yet per project memory).
        // If a tenant later crosses 100 projects we should add real pagination.
        listProjects(1, 100, searchQuery || undefined),
        listAlarmSummary().catch((err) => {
          // Don't block the dashboard if monitoring is down; just show no badges.
          console.warn('[dashboard] alarm summary failed:', err);
          return { counts: {} };
        }),
      ]);
      if (!isMountedRef.current) return;
      setProjects(projectsRes.items);
      setAlarmCounts(alarmsRes.counts);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh(debouncedSearch);
  }, [debouncedSearch, refresh]);

  // Organizations for the move panel — admin only, once per mount. A failure
  // leaves an empty list (the panel says so); it never blocks the dashboard.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    listAdminOrganizations()
      .then((orgs) => { if (!cancelled) setAdminOrgs(orgs); })
      .catch((err) => {
        console.warn('[dashboard] organizations failed:', err);
        if (!cancelled) setAdminOrgs([]);
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  // "Last capture" per site — fetched once per mount, separately from the
  // list: the server-side lookup scans every indication row (~1 s on prod)
  // and must not delay the sidebar. null = not loaded yet (tickets show no
  // capture text); a failure simply leaves it null.
  useEffect(() => {
    let cancelled = false;
    listSiteCaptureDates()
      .then((dates) => { if (!cancelled) setCaptureDates(dates); })
      .catch((err) => console.warn('[dashboard] capture dates failed:', err));
    return () => { cancelled = true; };
  }, []);

  // First-mount backfill: silently ask the backend to auto-derive coordinates
  // for any project in the user's tenant that doesn't have a pin yet. Existing
  // projects with saved map views or imported orthophotos get pinned without
  // the user needing to click "Set location" on each. Runs once per mount; if
  // anything was updated, we refetch so the new pins appear.
  //
  // No `cancelled` flag on the refetch path — StrictMode's synthetic unmount
  // would set it true and silently swallow the refetch. The backfill itself
  // is idempotent (server short-circuits on existing coords), and calling
  // refresh() after an unmount is a React 18 no-op, so unconditional is safe.
  const didBackfillRef = useRef(false);
  useEffect(() => {
    if (didBackfillRef.current) return;
    didBackfillRef.current = true;
    (async () => {
      try {
        const result = await deriveSiteCoordinates();
        if (result.updated > 0) {
          await refresh(debouncedSearch);
        }
      } catch (err) {
        // Backfill is a nice-to-have — never block the dashboard.
        console.warn('[dashboard] coordinate backfill failed:', err);
      }
    })();
    // Intentionally NOT depending on debouncedSearch — we only want this to
    // run on first mount, not on every search keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter chips — applied client-side on top of the server-side search.
  // Attach the separately-fetched capture day so every consumer (sidebar,
  // client panel, map) sees one item shape. undefined until loaded.
  const projectsWithCapture = useMemo<SiteListItem[]>(
    () => projects.map((p) => ({
      ...p,
      last_capture_date: captureDates ? captureDates[p.id] ?? null : undefined,
    })),
    [projects, captureDates]
  );

  const filteredProjects = useMemo(() => {
    const now = Date.now();
    return projectsWithCapture.filter((p) => {
      if (filter === 'alarms') {
        const c = alarmCounts[p.id];
        if (!c || c.critical + c.warning === 0) return false;
      } else if (filter === 'recent') {
        if (!p.updated_at) return false;
        const dt = new Date(p.updated_at).getTime();
        if (Number.isNaN(dt) || now - dt > SEVEN_DAYS_MS) return false;
      }
      return true;
    });
  }, [projectsWithCapture, alarmCounts, filter]);

  // Platform admin: group by client (see clientGroups.ts). Org users keep the
  // flat list — their tenant is normally a single shadow client, so a client
  // list of one would only add a click.
  const groupByClient = isAdmin;
  const clientGroups = useMemo(
    () => (groupByClient ? groupProjectsByClient(filteredProjects, alarmCounts) : []),
    [groupByClient, filteredProjects, alarmCounts]
  );
  const activeClient = groupByClient ? selectedClient : null;

  // Map pins follow the sidebar: every project, or only the open client's.
  const mapProjects = useMemo(() => {
    if (!activeClient) return filteredProjects;
    return filteredProjects.filter((p) => clientKeyOf(p) === activeClient.id);
  }, [activeClient, filteredProjects]);

  // Project selection — load via shared hook then navigate.
  const handleSelectProject = useCallback(
    async (site: SiteListItem) => {
      const ok = await openProject(site);
      if (ok) navigate('/editor');
    },
    [openProject, navigate]
  );

  // New project created → open it straight into the editor (smooth onboarding).
  // Falls back to refreshing the dashboard list if the load fails.
  const handleProjectCreated = useCallback(
    async (siteId: string, siteName: string) => {
      setCreateOpen(false);
      const ok = await openProject({ id: siteId, name: siteName });
      if (ok) navigate('/editor');
      else refresh(debouncedSearch);
    },
    [openProject, navigate, refresh, debouncedSearch]
  );

  // Move to another organization (admin). The server does the atomic
  // re-point and audit; on success the list is re-fetched so the site
  // regroups under its new client label. Resolves true to close the panel.
  const handleMoveProject = useCallback(
    async (site: SiteListItem, targetOrgId: string, confirmName: string): Promise<boolean> => {
      const targetName = adminOrgs?.find((o) => o.id === targetOrgId)?.name ?? 'the selected organization';
      try {
        const res = await moveProject(site.id, { targetOrgId, confirmName });
        addNotification({
          type: 'success',
          title: res.moved ? `Moved "${site.name}" to ${targetName}` : `"${site.name}" already belongs to ${targetName}`,
        });
        await refresh(debouncedSearch);
        return true;
      } catch (err) {
        const status = (err as { status?: number })?.status;
        const message =
          status === 409 ? 'This project is archived or changed meanwhile. Restore it first, or reload and retry.'
          : status === 404 ? 'That project or organization no longer exists.'
          : status === 403 ? 'You do not have permission to move this project.'
          : status === 400 ? 'Type the exact project name and choose an organization.'
          : err instanceof Error ? err.message : 'Unknown error';
        addNotification({ type: 'error', title: 'Could not move project', message });
        return false;
      }
    },
    [adminOrgs, addNotification, refresh, debouncedSearch]
  );

  // Manual pin — flip to place-mode, sidebar pill is the entry point.
  const handleRequestSetLocation = useCallback((site: SiteListItem) => {
    setPlaceMode({ siteId: site.id, siteName: site.name });
  }, []);

  // Inline edit (admin) — name / location / capacity. Optimistic: the ticket
  // shows the new values at once and rolls back if PUT /sites/:id fails.
  // Resolves true on success so the ticket can close its editor; false keeps
  // it open for a retry. `patch` holds only the changed fields (null clears).
  const handleEditProject = useCallback(
    async (site: SiteListItem, patch: UpdateProjectInput): Promise<boolean> => {
      const merge = (values: Partial<SiteListItem>) => (p: SiteListItem) =>
        p.id === site.id ? { ...p, ...values } : p;
      const previous: Partial<SiteListItem> = {
        name: site.name,
        location: site.location,
        capacity_kw: site.capacity_kw,
      };
      const optimistic: Partial<SiteListItem> = {};
      if (patch.name !== undefined) optimistic.name = patch.name;
      if (patch.location !== undefined) optimistic.location = patch.location ?? undefined;
      if (patch.capacityKw !== undefined) optimistic.capacity_kw = patch.capacityKw ?? undefined;
      setProjects((prev) => prev.map(merge(optimistic)));
      try {
        const updated = await updateProject(site.id, patch);
        setProjects((prev) =>
          prev.map(merge({
            name: updated.name || site.name,
            location: updated.location ?? undefined,
            capacity_kw: updated.capacity_kw ?? undefined,
            updated_at: updated.updated_at ?? site.updated_at,
          }))
        );
        const changes = [
          patch.name !== undefined ? `renamed to "${updated.name || patch.name}"` : null,
          patch.location !== undefined
            ? (patch.location ? `location "${patch.location}"` : 'location cleared')
            : null,
          patch.capacityKw !== undefined
            ? (patch.capacityKw ? `${patch.capacityKw} kW` : 'capacity cleared')
            : null,
        ].filter(Boolean).join(', ');
        addNotification({
          type: 'success',
          title: 'Project updated',
          message: `"${site.name}": ${changes}`,
        });
        return true;
      } catch (err) {
        setProjects((prev) => prev.map(merge(previous)));
        addNotification({
          type: 'error',
          title: 'Could not update project',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
        return false;
      }
    },
    [addNotification]
  );

  const handleLogout = useCallback(async () => {
    await serverLogout();
    navigate('/login', { replace: true });
  }, [navigate]);

  const handlePlaceLocation = useCallback(
    async (siteId: string, lng: number, lat: number) => {
      try {
        await setSiteLocation(siteId, lat, lng);
        // Optimistic local update — pin appears immediately, no re-fetch.
        setProjects((prev) =>
          prev.map((p) =>
            p.id === siteId
              ? { ...p, latitude: lat, longitude: lng, location_source: 'manual' }
              : p
          )
        );
        const site = projects.find((p) => p.id === siteId);
        addNotification({
          type: 'success',
          title: 'Location set',
          message: site ? `Pinned "${site.name}"` : 'Project pinned',
        });
      } catch (err) {
        addNotification({
          type: 'error',
          title: 'Could not set location',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      } finally {
        setPlaceMode(null);
      }
    },
    [projects, addNotification]
  );

  return (
    <div className="dashboard-page">
      <DashboardSidebar
        projects={filteredProjects}
        alarmCounts={alarmCounts}
        search={search}
        onSearchChange={setSearch}
        filter={filter}
        onFilterChange={setFilter}
        hoveredId={hoveredId}
        onHoverChange={setHoveredId}
        onSelectProject={handleSelectProject}
        onRequestSetLocation={handleRequestSetLocation}
        onEditProject={isAdmin ? handleEditProject : undefined}
        onMoveProject={isAdmin ? handleMoveProject : undefined}
        adminOrgs={adminOrgs}
        groupByClient={groupByClient}
        clientGroups={clientGroups}
        selectedClient={activeClient}
        onSelectClient={setSelectedClient}
        loading={loading}
        loadingProjectId={loadingId}
        totalCount={projects.length}
        onNewProject={() => setCreateOpen(true)}
        onOpenOrgSettings={() => setOrgSettingsOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenAdmin={() => navigate('/admin')}
        isAdmin={isAdmin}
        hasOrg={hasOrg}
        onLogout={handleLogout}
        userEmail={userEmail}
      />
      <main className="dashboard-page__map">
        {error && (
          <div className="dashboard-page__error" role="alert">
            {error}
            <button onClick={() => refresh(debouncedSearch)} className="dashboard-page__error-retry">Retry</button>
          </div>
        )}
        <DashboardMap
          projects={mapProjects}
          focusKey={activeClient?.id ?? null}
          alarmCounts={alarmCounts}
          hoveredId={hoveredId}
          onPinClick={(id) => {
            const site = projects.find((p) => p.id === id);
            if (site) handleSelectProject(site);
          }}
          onHoverChange={setHoveredId}
          placeMode={placeMode}
          onPlaceLocation={handlePlaceLocation}
          onViewSaved={() => addNotification({
            type: 'success',
            title: 'Default view saved',
            message: 'The dashboard will open at this view on your next visit.',
          })}
          onViewCleared={() => addNotification({
            type: 'info',
            title: 'Saved view cleared',
            message: 'The dashboard will fit to all your projects on next load.',
          })}
        />
        {placeMode && (
          <button
            className="dashboard-page__cancel-place"
            onClick={() => setPlaceMode(null)}
          >
            Cancel
          </button>
        )}
      </main>

      <CreateProjectModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleProjectCreated}
      />
      {orgSettingsOpen && <OrgSettingsPanel onClose={() => setOrgSettingsOpen(false)} />}
      {teamOpen && <TeamPanel onClose={() => setTeamOpen(false)} />}

      {/* Toast renderer — without this, project-open failures and the
          set-location/save-view notifications were invisible on /dashboard. */}
      <NotificationToasts />
    </div>
  );
}
